import { createPropertySelectors, createSelector } from '@ngxs/store';
import { denormalize } from 'normalizr';
import _ from 'lodash';

import { TicketItemsState, TicketItemsStateModel } from './ticket-items.state';
import { TicketItem, ticketItemsSchema } from 'src/app/models';
import { TicketsStateSelectors } from 'src/app/store/tickets/tickets.selectors';

export class TicketItemsStateSelectors {
  static slices =
    createPropertySelectors<TicketItemsStateModel>(TicketItemsState);

  private static selectedTicketItem = createSelector(
    [TicketItemsStateSelectors.slices.selectedTicketItem],
    (selectedTicketItem) => selectedTicketItem
  );

  private static ticketItems = createSelector(
    [TicketItemsStateSelectors.slices.ticketItems],
    (ticketItems) => ticketItems
  );

  static ticketItemsById = createSelector(
    [TicketItemsStateSelectors.ticketItems],
    (ticketItems) => ticketItems.byId
  );

  static getSelectedTicketItemId = createSelector(
    [TicketItemsStateSelectors.selectedTicketItem],
    (selectedTicketItemId) => selectedTicketItemId
  );

  static getTicketItems = createSelector(
    [TicketItemsStateSelectors.ticketItems],
    (ticketItems) => ticketItems
  );

  static getWaitingTicketItem = createSelector(
    [TicketItemsStateSelectors.slices.waitingTicketItem],
    (waitingTicketItem) => waitingTicketItem
  );

  static isSelectedTicketItem = createSelector(
    [TicketItemsStateSelectors.getSelectedTicketItemId],
    (selectedTiUuid) => (id: string) => id === selectedTiUuid
  );

  static getSelectedTicketItem = createSelector(
    [
      TicketItemsStateSelectors.getSelectedTicketItemId,
      TicketItemsStateSelectors.ticketItemsById
    ],
    (selectedTicketItem, ticket_items) => {
      if (selectedTicketItem == null) {
        return null;
      }

      const denormalizedTicketItem: TicketItem = denormalize(
        selectedTicketItem,
        ticketItemsSchema,
        {
          ticket_items
        }
      );
      return denormalizedTicketItem;
    }
  );

  static isLoadingPrice = createSelector(
    [
      TicketItemsStateSelectors.getWaitingTicketItem,
      TicketsStateSelectors.selectedTicketId
    ],
    (waitingTicketItems, deviceTicketUuid) =>
      waitingTicketItems?.length &&
      _.first(waitingTicketItems)?.device_ticket_uuid === deviceTicketUuid
  );

  static havingItemAddingInProgress = createSelector(
    [TicketItemsStateSelectors.slices.havingItemAddingInProgress],
    (havingItemAddingInProgress) => havingItemAddingInProgress
  );

  static isTicketHavingItemAddingInProgress = createSelector(
    [
      TicketItemsStateSelectors.havingItemAddingInProgress,
      TicketsStateSelectors.selectedTicketId,
      TicketsStateSelectors.normalizedTicket
    ],
    (havingItemAddingInProgress, selectedTicketUuid, selectedTicket) => {
      if (!selectedTicketUuid || !selectedTicket) {
        return false;
      }
      const tableTicketKey = `${
        selectedTicket.table_id || 0
      }_${selectedTicketUuid}`;
      return havingItemAddingInProgress?.includes(tableTicketKey) || false;
    }
  );

  static isTableHavingItemAddingInProgress = createSelector(
    [TicketItemsStateSelectors.havingItemAddingInProgress],
    (havingItemAddingInProgress) => (tableId: number) => {
      if (!tableId) {
        return false;
      }
      const tablePrefix = `${tableId || 0}_`;
      return (
        havingItemAddingInProgress?.some((key) =>
          key.startsWith(tablePrefix)
        ) || false
      );
    }
  );

  static getTicketItemByUuid = createSelector(
    [TicketItemsStateSelectors.ticketItemsById],
    (ticketItemsById) => (ticketItemUuid: string) =>
      ticketItemsById && ticketItemsById[ticketItemUuid]
  );

  // move from self-service.selector.ts
  static getIsEmptyTicket = createSelector(
    [TicketItemsStateSelectors.ticketItemsById],
    (ticketItems) => _.every(ticketItems, (ti) => !ti.created_at)
  );
}
